import React, { useEffect, useState } from 'react';
import User from '../../membership/models/User';
import MembershipService from '../../membership/services/MembershipService';
import ProfileService from '../../shared/services/ProfileService';

/** Allowed type for indexing cache values. */
type CacheKeyT = string | number;
/** Lightweight caching of user data for avatars. */
class MemoryCache<T> {
  /** Cached values. */
  private cache: { [key: CacheKeyT]: { value: T; expires: number } } = {};
  /** Expiry duration, in seconds, of cache items. */
  private expires = 300;
  /** Method invoked to load a value. */
  private load: (key: CacheKeyT) => Promise<T> = null;
  /** Combine multiple cache loading. */
  private pending: { [key: CacheKeyT]: Promise<T> } = {};

  /**
   * Initialize a new instance of the cache.
   * @param load Method invoked to load a missing or expired cache value.
   * @param expires Expiry duration, in seconds, of cache items.
   */
  constructor(load: (key: CacheKeyT) => Promise<T>, expires: number = 300) {
    this.load = load;
    this.expires = expires;
  }

  /** Retrieve a value from cache or load. */
  public async get(key: CacheKeyT): Promise<T> {
    const now = new Date().valueOf();
    const item = this.cache[key];
    if (item && item.expires > now) {
      return item.value;
    }
    if (!this.pending[key]) {
      this.pending[key] = this.load(key).then((value) => {
        this.cache[key] = { value, expires: now + this.expires * 1e3 };
        delete this.pending[key];
        return value;
      });
    }
    return this.pending[key];
  }

  /** Remove the cached value. */
  public remove(key: string | number) {
    delete this.cache[key];
  }
}

/** Static cache of loaded user information, used to build initials. */
const userCache = new MemoryCache<User>(MembershipService.getUserByKeyAsync);
const avatarCache = new MemoryCache<string>(MembershipService.getAvatarUrl);

/**
 * Use and cache avatars from the Membership / Asset service.
 * @param userkey Identifier of user whose avatar to load.
 */
const useAvatars = (userkey: number) => {
  /** User account. */
  const [user, setUser] = useState<User | undefined>();
  /** User's friendly name, which can be used as the image alt attribute. */
  const [friendlyname, setFriendlyname] = useState<string>('');
  /** User's initials, which can be used in a placeholder avatar. */
  const [initials, setInitials] = useState<string>('');
  /** User avatar image URL including sas token. */
  const [src, setSrc] = useState<string | undefined>();

  useEffect(() => {
    if (userkey) {
      avatarCache.get(userkey).then(setSrc);
      userCache.get(userkey).then(setUser);
    }
  }, [userkey]);

  useEffect(() => {
    setFriendlyname(
      user && (user.friendlyname || `${user.firstname} ${user.lastname}`)
    );
    setInitials(
      user && ProfileService.getInitials(user.firstname, user.lastname)
    );
  }, [user]);

  return {
    friendlyname,
    initials,
    src
  };
};

export default useAvatars;
